import { useTranslation } from 'react-i18next';
import type { AIClipStatus, AIClipTaskSummary } from '../../../api/aiClip';

interface AIClipHistoryListProps {
  tasks: AIClipTaskSummary[];
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreFailed: boolean;
  onLoadMore: () => void;
  onSelectResult: (taskId: string) => void;
}

const toneFor = (status: AIClipStatus): 'success' | 'danger' | 'neutral' | 'progress' => {
  if (status === 'succeeded') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'cancelled') return 'neutral';
  return 'progress';
};

const toneColors = {
  success: { background: 'rgba(22, 163, 74, 0.12)', color: '#15803d' },
  danger: { background: 'rgba(220, 38, 38, 0.12)', color: '#b91c1c' },
  neutral: { background: 'rgba(100, 116, 139, 0.14)', color: '#64748b' },
  progress: { background: 'rgba(2, 132, 199, 0.12)', color: '#0369a1' },
};

const AIClipHistoryList = ({
  tasks, hasMore, isLoadingMore, loadMoreFailed, onLoadMore, onSelectResult,
}: AIClipHistoryListProps) => {
  const { t, i18n } = useTranslation('episodeDetail');
  const formatTime = (value?: string) => value
    ? new Date(value).toLocaleString(i18n.language)
    : '—';

  return (
    <section aria-label={t('aiClip.workspace.history')} className="ai-clip-history-section">
      <div className="ai-clip-history-heading">
        <h3>{t('aiClip.workspace.history')}</h3>
      </div>
      {tasks.length === 0 && (
        <div className="ai-clip-history-empty">
          <span aria-hidden="true">◇</span>
          <p>{t('aiClip.workspace.empty')}</p>
        </div>
      )}
      <div className="ai-clip-history-list">
        {tasks.map(task => {
          const tone = toneFor(task.status);
          return (
            <article
              key={task.id}
              data-status={task.status}
              data-tone={tone}
              className="ai-clip-history-item"
            >
              <div className="ai-clip-history-item__top">
                <div className="ai-clip-history-item__identity">
                  <strong>{t('aiClip.workspace.attempt', { number: task.attemptNo })}</strong>
                  <span className="ai-clip-status-pill" style={toneColors[tone]}>
                    {t(`aiClip.workspace.status.${task.status}`)}
                  </span>
                </div>
                {task.status === 'succeeded' && (
                  <button
                    type="button"
                    className="btn btn-secondary ai-clip-history-view"
                    onClick={() => onSelectResult(task.id)}
                  >
                    {t('aiClip.workspace.viewResult')}
                  </button>
                )}
              </div>
              <div className="ai-clip-history-item__meta">
                <span>{t('aiClip.workspace.createdBy')}: {task.createdByUsername || t('aiClip.workspace.unknownUser')}</span>
                <span>{t('aiClip.workspace.submittedAt')}: {formatTime(task.createdAt)}</span>
                {task.completedAt && <span>{t('aiClip.workspace.completedAt')}: {formatTime(task.completedAt)}</span>}
              </div>
              {task.status === 'failed' && (
                <div className="ai-clip-history-error">
                  <p>{task.errorMessage || t('aiClip.workspace.status.failed')}</p>
                  {task.errorCode && (
                    <details>
                      <summary>{t('aiClip.workspace.errorDetails')}</summary>
                      <code>{task.errorCode}</code>
                    </details>
                  )}
                </div>
              )}
              {task.status === 'cancelled' && task.errorMessage && (
                <p className="ai-clip-history-cancelled">{task.errorMessage}</p>
              )}
            </article>
          );
        })}
      </div>

      {hasMore && (
        <div className="ai-clip-history-load-more">
          {loadMoreFailed && <p>{t('aiClip.workspace.loadMoreFailed')}</p>}
          <button type="button" className="btn btn-secondary" disabled={isLoadingMore} onClick={onLoadMore}>
            {loadMoreFailed ? t('aiClip.retry') : t('aiClip.workspace.loadMore')}
          </button>
        </div>
      )}
    </section>
  );
};

export default AIClipHistoryList;
